import { 
  Box, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Typography,
  Chip,
  Avatar,
  Stack,
  LinearProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import CancelIcon from '@mui/icons-material/Cancel';

interface Member {
  member_name: string;
  member_email: string;
  team_name: string;
  total_quizzes: number;
  completed_quizzes: number;
  pending_quizzes: number;
  completion_rate: string | number;
  total_xp: number;
  avg_score: string | number;
  last_quiz_date: string | null;
}

interface ParticipationTableProps {
  members: Member[];
  title?: string;
}

const getStatusChip = (member: Member) => {
  if (member.total_quizzes === 0) {
    return (
      <Chip 
        icon={<CancelIcon sx={{ fontSize: 16 }} />}
        label="No Activity" 
        size="small"
        sx={{ 
          backgroundColor: '#FEE2E2',
          color: '#DC2626',
          fontWeight: 500,
          fontSize: '0.75rem',
        }}
      />
    );
  }
  if (member.pending_quizzes > 0) {
    return (
      <Chip 
        icon={<PendingIcon sx={{ fontSize: 16 }} />}
        label={`${member.pending_quizzes} Pending`}
        size="small"
        sx={{ 
          backgroundColor: '#FEF3C7',
          color: '#D97706',
          fontWeight: 500,
          fontSize: '0.75rem',
        }}
      />
    );
  }
  return (
    <Chip 
      icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
      label="Complete" 
      size="small"
      sx={{ 
        backgroundColor: '#D1FAE5',
        color: '#059669',
        fontWeight: 500,
        fontSize: '0.75rem',
      }}
    />
  );
};

const getInitials = (name: string) => {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const getAvatarColor = (name: string) => {
  const colors = ['#0099CC', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444', '#EC4899'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

export const ParticipationTable = ({ members, title = 'Team Participation' }: ParticipationTableProps) => {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '16px',
        border: '1px solid #E5E7EB',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ p: 3, borderBottom: '1px solid #E5E7EB' }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#2C3E50' }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5 }}>
          Track quiz completion and performance by team member
        </Typography>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#F9FAFB' }}>
              <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Member</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Status</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, color: '#374151' }}>Progress</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, color: '#374151' }}>Avg Score</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, color: '#374151' }}>XP</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {members.map((member, index) => (
              <TableRow 
                key={index}
                sx={{ 
                  '&:hover': { backgroundColor: '#F9FAFB' },
                  '&:last-child td': { borderBottom: 0 },
                }}
              >
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar 
                      sx={{ 
                        width: 40, 
                        height: 40, 
                        backgroundColor: getAvatarColor(member.member_name),
                        fontSize: '0.875rem',
                        fontWeight: 600,
                      }}
                    >
                      {getInitials(member.member_name)}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#2C3E50' }}>
                        {member.member_name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                        {member.member_email}
                      </Typography>
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell>
                  {getStatusChip(member)}
                </TableCell>
                <TableCell align="center">
                  <Stack spacing={0.5} sx={{ minWidth: 120 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={parseFloat(String(member.completion_rate)) || 0}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: '#E5E7EB',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 4,
                          backgroundColor: parseFloat(String(member.completion_rate)) === 100 
                            ? '#10B981' 
                            : '#0099CC',
                        },
                      }}
                    />
                    <Typography variant="caption" sx={{ color: '#6B7280' }}>
                      {member.completed_quizzes}/{member.total_quizzes} ({member.completion_rate}%)
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell align="center">
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 600,
                      color: parseFloat(String(member.avg_score)) >= 80 ? '#10B981' : 
                             parseFloat(String(member.avg_score)) >= 60 ? '#F59E0B' : '#EF4444',
                    }}
                  >
                    {member.total_quizzes > 0 ? `${member.avg_score}%` : '-'}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#2C3E50' }}>
                    {member.total_xp}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};
